import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, View, ViewStyle, useWindowDimensions } from 'react-native';
import { FontFamily } from '../../../../constants/fonts';
import { useTheme } from '../../../../theme';
import MetricCard from '../../components/MetricCard';
import SectionRetry from '../../components/SectionRetry';
import type {
  AgingCardData,
  CategoryBarData,
  DuplicateCardData,
  StatCardData,
  StatusCardData,
  TicketingOverviewPayload,
} from '../../components/types';
import VerticalBarChartCard from '../../components/VerticalBarChartCard';

/** Independent { data, loading, error, retry } lifecycle for one Overview section. */
interface OverviewSectionState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

interface OverviewProps {
  coreMetrics: OverviewSectionState<TicketingOverviewPayload['overview']>;
  statusBreakdown: OverviewSectionState<TicketingOverviewPayload['statusBreakdown']>;
  agingTickets: OverviewSectionState<TicketingOverviewPayload['agingTickets']>;
  categoryBreakdown: OverviewSectionState<TicketingOverviewPayload['ticketsByCategory']>;
  duplicateActivity: OverviewSectionState<TicketingOverviewPayload['duplicateActivity']>;
  hasActiveFilters?: boolean;
}

const OVERVIEW_CARD_CONFIG: Array<Omit<StatCardData, 'value'>> = [
  { label: 'Total Tickets', icon: 'confirmation-number', iconBg: '#EAF1FB', iconColor: '#005CBC' },
  { label: 'Total Closed', icon: 'check-circle', iconBg: '#E6F4EA', iconColor: '#1E8735' },
  { label: 'Open Tickets', icon: 'folder-open', iconBg: '#FFF8E1', iconColor: '#F59E0B' },
  { label: 'Critical Tickets', icon: 'error', iconBg: '#FDECEA', iconColor: '#D32F2F' },
  { label: 'Open Access Requests', icon: 'lock-open', iconBg: '#F3E5F5', iconColor: '#7B1FA2' },
  { label: 'Average Resolution Time', icon: 'timer', iconBg: '#E3F2FD', iconColor: '#1565C0', subLabel: 'hours' },
  { label: 'Average Time in New/In Progress', icon: 'hourglass-empty', iconBg: '#FFF3E0', iconColor: '#E65100', subLabel: 'hours' },
  { label: 'IT Escalation Rate', icon: 'computer', iconBg: '#E8EAF6', iconColor: '#3949AB' },
  { label: 'ADO Escalation Rate', icon: 'trending-up', iconBg: '#FBE9E7', iconColor: '#BF360C' },
  { label: 'Duplicates Merged', icon: 'merge-type', iconBg: '#E0F2F1', iconColor: '#00695C' },
];

const STATUS_COLORS: Record<string, string> = {
  New: '#5B8DEF',
  'In Progress': '#F59E0B',
  'Escalated to IT': '#EF4444',
  'Escalated to TMV': '#8B5CF6',
  'Submitted to ADO': '#EC4899',
  'Waiting on User': '#6B7280',
  Closed: '#1E8735',
};

const AGING_COLORS: Record<string, string> = {
  '0 to 4 hours': '#1E8735',
  '4 to 24 hours': '#F59E0B',
  '1 to 3 days': '#EF4444',
  '3+ days': '#7C1D1D',
};

const STATUS_ORDER = ['New', 'In Progress', 'Escalated to IT', 'Escalated to TMV', 'Submitted to ADO', 'Waiting on User', 'Closed'];
const AGING_ORDER = ['0 to 4 hours', '4 to 24 hours', '1 to 3 days', '3+ days'];
const DUPLICATE_ORDER = ['Potential Duplicates', 'Merged Tickets', 'False Positives', 'Avg Time Between Dupes'];

const normalizeLabel = (value: string): string => value.trim().toLowerCase();

const formatMetricValue = (value: number, unit: string): string => {
  if (unit === 'percent') {
    return `${value.toFixed(1)}%`;
  }

  if (unit === 'hours') {
    return value.toFixed(1);
  }

  return value.toLocaleString();
};

const formatTrendValue = (value: number, unit: string): string => {
  if (unit === 'percent') {
    return `${value.toFixed(1)}%`;
  }

  return value.toFixed(1);
};

const formatDuplicateValue = (
  count?: number,
  percentage?: number | null,
  value?: number,
  unit?: string,
): { value: string; valueSuffix?: string } => {
  if (typeof count === 'number') {
    if (typeof percentage === 'number') {
      return {
        value: count.toLocaleString(),
        valueSuffix: `${percentage.toFixed(1)}%`,
      };
    }

    return { value: count.toLocaleString() };
  }

  if (typeof value === 'number') {
    if (unit === 'hours') {
      return {
        value: value.toFixed(1),
        valueSuffix: 'hrs',
      };
    }

    if (unit === 'percent') {
      return { value: `${value.toFixed(1)}%` };
    }

    return { value: value.toFixed(1) };
  }

  return { value: '—' };
};

const getResponsiveColumns = (availableWidth: number, minCardWidth: number, maxColumns: number) =>
  Math.max(1, Math.min(maxColumns, Math.floor((availableWidth + 14) / (minCardWidth + 14))));

const getItemStyle  = (columns: number): ViewStyle => ({
  width: `${100 / columns}%`,
  paddingHorizontal: 7,
  marginBottom: 14,
});



const Overview: React.FC<OverviewProps> = ({
  coreMetrics,
  statusBreakdown,
  agingTickets,
  categoryBreakdown,
  duplicateActivity,
  hasActiveFilters = false,
}) => {
  const [categoryView, setCategoryView] = useState<'total' | 'open'>('total');
  const { width } = useWindowDimensions();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const overviewCards = useMemo<StatCardData[]>(() => {
    const apiOverview = coreMetrics.data ?? [];

    return OVERVIEW_CARD_CONFIG.map((card) => {
      const matched = apiOverview.find((metric) => normalizeLabel(metric.title) === normalizeLabel(card.label));

      return {
        ...card,
        value: matched ? formatMetricValue(matched.value, matched.unit) : '—',
        trendText: matched?.trend ? formatTrendValue(matched.trend.value, matched.trend.unit) : undefined,
        trendDirection: matched?.trend?.direction,
        trendImpact: matched?.trend?.impact,
        description: matched?.description,
      };
    });
  }, [coreMetrics.data]);

  const statusCards = useMemo<StatusCardData[]>(() => {
    const apiStatus = statusBreakdown.data ?? [];

    // While the API call is in flight (and no data yet) render placeholder
    // entries so the skeleton grid keeps the final layout shape.
    if (!apiStatus.length && statusBreakdown.loading) {
      return STATUS_ORDER.map((status) => ({
        label: status,
        count: '—',
        avgTime: '—',
        color: STATUS_COLORS[status] ?? '#5B8DEF',
      }));
    }


    const orderIndexByStatus = new Map(STATUS_ORDER.map((status, index) => [status, index]));
    const sortedStatus = [...apiStatus].sort((a, b) => {
      const aOrder = orderIndexByStatus.get(a.status) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = orderIndexByStatus.get(b.status) ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });

    return sortedStatus.map((item) => ({
      label: item.status,
      count: item.count.toLocaleString(),
      avgTime: item.description || '—',
      color: STATUS_COLORS[item.status] ?? '#5B8DEF',
    }));
  }, [statusBreakdown.data, statusBreakdown.loading]);

  const agingCards = useMemo<AgingCardData[]>(() => {
    const apiAging = agingTickets.data ?? [];

    // Placeholder buckets keep the skeleton grid aligned with the final layout.
    if (!apiAging.length && agingTickets.loading) {
      return AGING_ORDER.map((bucket) => ({
        bucket,
        count: '—',
        description: '—',
        color: AGING_COLORS[bucket] ?? '#6B7280',
      }));
    }

    const orderIndexByBucket = new Map(AGING_ORDER.map((bucket, index) => [bucket, index]));

    const sortedAging = [...apiAging].sort((a, b) => {
      const aOrder = orderIndexByBucket.get(a.bucket) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = orderIndexByBucket.get(b.bucket) ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });

    return sortedAging.map((item) => ({
      bucket: item.bucket,
      count: item.count.toLocaleString(),
      description: item.description || 'Open tickets in bucket',
      color: AGING_COLORS[item.bucket] ?? '#6B7280',
    }));
  }, [agingTickets.data, agingTickets.loading]);


  const categoryBars = useMemo<CategoryBarData[]>(() => {
    const source = categoryBreakdown.data ?? [];
    const preferred = source.find((item) => item.view === categoryView);
    const fallback = source.find((item) => item.view === 'total') ?? source[0];
    const selectedView = preferred ?? fallback;

    return (selectedView?.categories ?? []).map((item) => ({
      name: item.name,
      count: item.count,
      percentage: item.percentage,
    }));
  }, [categoryBreakdown.data, categoryView]);

  const duplicateCards = useMemo<DuplicateCardData[]>(() => {
    const cards = (duplicateActivity.data ?? []).map((item) => ({
      metric: item.metric,
      ...formatDuplicateValue(item.count, item.percentage, item.value, item.unit),
      description: item.description,
    }));

    // Placeholder metrics keep the skeleton grid aligned with the final layout.
    if (!cards.length && duplicateActivity.loading) {
      return DUPLICATE_ORDER.map((metric) => ({
        metric,
        value: '—',
        description: '—',
      }));
    }


    const orderIndexByMetric = new Map(DUPLICATE_ORDER.map((metric, index) => [metric, index]));
    return cards.sort((a, b) => {
      const aOrder = orderIndexByMetric.get(a.metric) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = orderIndexByMetric.get(b.metric) ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
  }, [duplicateActivity.data, duplicateActivity.loading]);


  const statColumns = getResponsiveColumns(width, 212, 5);
  const statusColumns = getResponsiveColumns(width, 160, 7);
  const agingColumns = getResponsiveColumns(width, 180, 4);
  const duplicateColumns = getResponsiveColumns(width, 212, 4);

  const statCardStyle = getItemStyle(statColumns);
  const statusCardStyle = getItemStyle(statusColumns);
  const agingCardStyle = getItemStyle(agingColumns);

  return (
    <View style={styles.overviewContent}>

      {coreMetrics.error ? (
        <SectionRetry message={coreMetrics.error} onRetry={coreMetrics.retry} retrying={coreMetrics.loading} />
      ) : (
        <View style={styles.statGrid}>
          {overviewCards.map((metric) => (
            <View key={metric.label} style={statCardStyle}>
              <MetricCard
                title={metric.label}
                value={metric.value}
                subtitle={metric.subLabel}
                description={metric.description}
                trendText={metric.trendText}
                trendDirection={metric.trendDirection}
                trendImpact={metric.trendImpact}
                loading={coreMetrics.loading}
              />

            </View>
          ))}
        </View>
      )}

      <Text style={[styles.sectionTitle, isDark && { color: '#B0B0B0' }]}>Status Breakdown</Text>
      {statusBreakdown.error ? (
        <SectionRetry message={statusBreakdown.error} onRetry={statusBreakdown.retry} retrying={statusBreakdown.loading} />
      ) : (
        <View style={styles.statusGrid}>
          {statusCards.map((item) => (
            <View key={item.label} style={statusCardStyle}>
              <MetricCard
                title={item.label}
                value={item.count}
                description={item.avgTime}
                loading={statusBreakdown.loading}
              />

            </View>
          ))}
        </View>
      )}

      <Text style={[styles.sectionTitle, isDark && { color: '#B0B0B0' }]}>
        Aging Tickets <Text style={[styles.sectionTitleSub, isDark && { color: '#B0B0B0' }]}>(Open)</Text>
      </Text>
      {agingTickets.error ? (
        <SectionRetry message={agingTickets.error} onRetry={agingTickets.retry} retrying={agingTickets.loading} />
      ) : (
        <View style={styles.agingGrid}>
          {agingCards.map((item) => (
            <View key={item.bucket} style={agingCardStyle}>
              <MetricCard
                title={item.bucket}
                value={item.count}
                valueColor={
                  item.bucket === '3+ days'
                    ? (isDark ? '#FF6B6B' : '#D32F2F')
                    : (isDark ? '#F2F2F2' : '#2D2F33')
                }
                description={item.description}
                loading={agingTickets.loading}
              />
            </View>
          ))}
        </View>
      )}

    <VerticalBarChartCard
      title="Total Tickets by Category"
      data={categoryBars.map(item => ({
        label: item.name,
        value: item.count,
        percentage: item.percentage,
        valueLabel: item.count.toLocaleString(),
      }))}
      metadataPosition="inside"
      toggleValue={categoryView}
      toggleOptions={['total', 'open']}
      onToggleChange={setCategoryView}
      loading={categoryBreakdown.loading}
      error={categoryBreakdown.error}
      onRetry={categoryBreakdown.retry}
      hasActiveFilters={hasActiveFilters}
    />

      <Text style={[styles.sectionTitle, isDark && { color: '#B0B0B0' }]}>Duplicate Activity</Text>

      {duplicateActivity.error ? (
        <SectionRetry message={duplicateActivity.error} onRetry={duplicateActivity.retry} retrying={duplicateActivity.loading} />
      ) : (
        <View style={styles.duplicateGrid}>
          {duplicateCards.map((item) => (
            <View key={item.metric} style={getItemStyle(duplicateColumns)}>
              <MetricCard
                title={item.metric}
                value={item.value}
                subtitle={item.valueSuffix}
                description={item.description}
                loading={duplicateActivity.loading}
              />
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  overviewContent: {
    paddingHorizontal: 0,
    paddingVertical: 2,
  },
  overviewMetaError: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#B42318',
    marginTop: 6,
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    color: '#6A6D71',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
   // marginTop: 20,
    marginBottom: 10,
  },
  sectionTitleSub: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#6A6D71',
    textTransform: 'none',
    letterSpacing: 0,
  },
  statGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 6,
    marginHorizontal: -7,
  },
  statusGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 6,
    marginHorizontal: -7,
  },
  agingGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 6,
    marginHorizontal: -7,
  },
  duplicateGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -7,
  },
});

export default Overview;